import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { calculateSLADueDate } from '@/config/sla'
import { useNotificationsStore } from '@/stores/notifications'
import type { TicketsState } from './state'
import type { Ticket, TicketType, TicketStatus, Priority } from '@/types'

export interface CreateTicketData {
  organizationId: string
  projectId?: string
  type: TicketType
  title: string
  description: string
  priority: Priority
  operatorIds?: string[]
  attachments?: string[]
}

export interface UpdateTicketData {
  projectId?: string
  type?: TicketType
  title?: string
  description?: string
  priority?: Priority
  status?: TicketStatus
  operatorIds?: string[]
  attachments?: string[]
}

const COLLECTION_NAME = 'tickets'

const convertTimestampToDate = (data: Record<string, unknown>): Record<string, unknown> => {
  const result = { ...data }
  const dateFields = [
    'createdAt',
    'updatedAt',
    'slaResponseDue',
    'slaResolutionDue',
    'respondedAt',
    'resolvedAt',
  ]

  dateFields.forEach((field) => {
    if (result[field] instanceof Timestamp) {
      result[field] = (result[field] as Timestamp).toDate()
    }
  })

  return result
}

let globalUnsubscribe: Unsubscribe | null = null

export const createActions = (state: TicketsState) => {
  const authStore = useAuthStore()
  const notificationsStore = useNotificationsStore()

  const subscribeGlobal = () => {
    if (globalUnsubscribe) return

    state.isLoading = true

    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(
      collection(db, COLLECTION_NAME),
      ...baseConstraints,
      orderBy('createdAt', 'desc')
    )

    if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
      if (authStore.user?.organizationId) {
        q = query(
          collection(db, COLLECTION_NAME),
          ...baseConstraints,
          where('organizationId', '==', authStore.user.organizationId),
          orderBy('createdAt', 'desc')
        )
      }
    } else if (authStore.user?.role === 'operator') {
      q = query(
        collection(db, COLLECTION_NAME),
        ...baseConstraints,
        where('operatorIds', 'array-contains', authStore.user.id),
        orderBy('createdAt', 'desc')
      )
    }

    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      state.tickets = snapshot.docs.map((doc) => {
        const data = convertTimestampToDate(doc.data())
        return {
          id: doc.id,
          ...data,
          operatorIds: (data.operatorIds as string[]) || [],
        } as Ticket
      })
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = () => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchTickets = async () => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      let q = query(collection(db, COLLECTION_NAME), ...baseConstraints, orderBy('createdAt', 'desc'))

      if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user?.organizationId) {
          q = query(
            collection(db, COLLECTION_NAME),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      } else if (authStore.user?.role === 'operator') {
        q = query(
          collection(db, COLLECTION_NAME),
          ...baseConstraints,
          where('operatorIds', 'array-contains', authStore.user.id),
          orderBy('createdAt', 'desc')
        )
      }

      const querySnapshot = await getDocs(q)

      state.tickets = querySnapshot.docs.map((doc) => {
        const data = convertTimestampToDate(doc.data())
        return {
          id: doc.id,
          ...data,
          operatorIds: (data.operatorIds as string[]) || [],
        } as Ticket
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching tickets'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const fetchTicketById = async (id: string): Promise<Ticket | null> => {
    state.isLoading = true
    state.error = null

    try {
      const docRef = doc(db, COLLECTION_NAME, id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = convertTimestampToDate(docSnap.data())
        const ticket = {
          id: docSnap.id,
          ...data,
          operatorIds: (data.operatorIds as string[]) || [],
        } as Ticket
        state.selectedTicket = ticket
        return ticket
      }
      return null
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching ticket'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const createTicket = async (data: CreateTicketData): Promise<Ticket> => {
    state.isLoading = true
    state.error = null

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      // Calculate SLA due dates based on priority
      const slaResponseDue = calculateSLADueDate(now, data.priority, 'response')
      const slaResolutionDue = calculateSLADueDate(now, data.priority, 'resolution')

      const ticketData = {
        ...getTenantData(),
        organizationId: data.organizationId,
        projectId: data.projectId || null,
        type: data.type,
        title: data.title,
        description: data.description,
        priority: data.priority,
        status: 'open' as TicketStatus,
        operatorIds: data.operatorIds || [],
        attachments: data.attachments || [],
        slaResponseDue: Timestamp.fromDate(slaResponseDue),
        slaResolutionDue: Timestamp.fromDate(slaResolutionDue),
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        createdBy: userId,
        updatedBy: userId,
      }

      const docRef = await addDoc(collection(db, COLLECTION_NAME), ticketData)

      const newTicket: Ticket = {
        id: docRef.id,
        organizationId: data.organizationId,
        projectId: data.projectId,
        type: data.type,
        title: data.title,
        description: data.description,
        priority: data.priority,
        status: 'open',
        operatorIds: data.operatorIds || [],
        attachments: data.attachments || [],
        slaResponseDue,
        slaResolutionDue,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId,
      }

      state.tickets.unshift(newTicket)

      // Notify admins + assigned operators about new ticket
      await notificationsStore.notifyAdminsAndStakeholders(
        newTicket.operatorIds,
        'ticket_created',
        'Nuevo ticket creado',
        `Se ha creado el ticket "${newTicket.title}"`,
        'ticket',
        newTicket.id
      )

      return newTicket
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error creating ticket'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateTicket = async (id: string, data: UpdateTicketData): Promise<Ticket> => {
    state.isLoading = true
    state.error = null

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      const updateData: Record<string, unknown> = {
        updatedAt: Timestamp.fromDate(now),
        updatedBy: userId,
      }

      // Handle optional fields - convert undefined to null for Firestore
      if (data.projectId !== undefined) updateData.projectId = data.projectId || null
      if (data.type !== undefined) updateData.type = data.type
      if (data.title !== undefined) updateData.title = data.title
      if (data.description !== undefined) updateData.description = data.description
      if (data.priority !== undefined) updateData.priority = data.priority
      if (data.status !== undefined) updateData.status = data.status
      if (data.operatorIds !== undefined) updateData.operatorIds = data.operatorIds
      if (data.attachments !== undefined) updateData.attachments = data.attachments

      const docRef = doc(db, COLLECTION_NAME, id)
      await updateDoc(docRef, updateData)

      // Update local state
      const index = state.tickets.findIndex((t) => t.id === id)
      if (index !== -1) {
        state.tickets[index] = {
          ...state.tickets[index],
          ...data,
          updatedAt: now,
          updatedBy: userId,
        }
      }

      if (state.selectedTicket?.id === id) {
        state.selectedTicket = {
          ...state.selectedTicket,
          ...data,
          updatedAt: now,
          updatedBy: userId,
        }
      }

      return state.tickets[index] || state.selectedTicket!
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error updating ticket'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteTicket = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const docRef = doc(db, COLLECTION_NAME, id)
      await deleteDoc(docRef)

      state.tickets = state.tickets.filter((t) => t.id !== id)

      if (state.selectedTicket?.id === id) {
        state.selectedTicket = null
      }
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error deleting ticket'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateTicketStatus = async (id: string, status: TicketStatus): Promise<Ticket> => {
    const now = new Date()
    const updateData: UpdateTicketData & { respondedAt?: Date; resolvedAt?: Date } = { status }

    // Track SLA milestones
    const ticket = state.tickets.find((t) => t.id === id)
    if (ticket) {
      // First response - mark responded time
      if (status === 'in_progress' && !ticket.respondedAt) {
        const slaResponseMet = now <= ticket.slaResponseDue
        await updateDoc(doc(db, COLLECTION_NAME, id), {
          respondedAt: Timestamp.fromDate(now),
          slaResponseMet,
        })

        const index = state.tickets.findIndex((t) => t.id === id)
        if (index !== -1) {
          state.tickets[index].respondedAt = now
          state.tickets[index].slaResponseMet = slaResponseMet
        }
      }

      // Resolution - mark resolved time
      if ((status === 'resolved' || status === 'closed') && !ticket.resolvedAt) {
        const slaResolutionMet = now <= ticket.slaResolutionDue
        await updateDoc(doc(db, COLLECTION_NAME, id), {
          resolvedAt: Timestamp.fromDate(now),
          slaResolutionMet,
        })

        const index = state.tickets.findIndex((t) => t.id === id)
        if (index !== -1) {
          state.tickets[index].resolvedAt = now
          state.tickets[index].slaResolutionMet = slaResolutionMet
        }
      }

      // Notify assigned operators + creator about status change
      if (status !== ticket.status) {
        const stakeholders = [...(ticket.operatorIds || []), ticket.createdBy].filter(Boolean) as string[]
        await notificationsStore.notifyUsers(
          stakeholders,
          'ticket_status_changed',
          'Ticket actualizado',
          `"${ticket.title}" cambió a ${status}`,
          'ticket',
          id
        )
      }
    }

    return updateTicket(id, updateData)
  }

  const assignTicket = async (id: string, operatorIds: string[]): Promise<Ticket> => {
    const ticket = state.tickets.find((t) => t.id === id)
    const previousIds = ticket?.operatorIds || []
    const result = await updateTicket(id, { operatorIds })

    // Notify newly assigned operators
    const newOperatorIds = operatorIds.filter((opId) => !previousIds.includes(opId))
    if (newOperatorIds.length > 0 && ticket) {
      await notificationsStore.notifyUsers(
        newOperatorIds,
        'ticket_assigned',
        'Ticket asignado',
        `Se te asignó el ticket "${ticket.title}"`,
        'ticket',
        id
      )
    }

    return result
  }

  const convertToRequirement = async (
    ticketId: string,
    requirementId: string
  ): Promise<Ticket> => {
    state.isLoading = true
    state.error = null

    try {
      const now = new Date()
      const userId = authStore.user?.id || ''

      const docRef = doc(db, COLLECTION_NAME, ticketId)
      await updateDoc(docRef, {
        convertedToRequirementId: requirementId,
        status: 'closed',
        updatedAt: Timestamp.fromDate(now),
        updatedBy: userId,
      })

      const index = state.tickets.findIndex((t) => t.id === ticketId)
      if (index !== -1) {
        state.tickets[index] = {
          ...state.tickets[index],
          convertedToRequirementId: requirementId,
          status: 'closed',
          updatedAt: now,
          updatedBy: userId,
        }
      }

      if (state.selectedTicket?.id === ticketId) {
        state.selectedTicket = {
          ...state.selectedTicket,
          convertedToRequirementId: requirementId,
          status: 'closed',
          updatedAt: now,
          updatedBy: userId,
        }
      }

      return state.tickets[index] || state.selectedTicket!
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error converting ticket'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const setFilters = (
    newFilters: Partial<TicketsState['filters']>
  ) => {
    Object.assign(state.filters, newFilters)
  }

  const clearFilters = () => {
    state.filters.search = ''
    state.filters.organizationId = ''
    state.filters.projectId = ''
    state.filters.type = ''
    state.filters.status = ''
    state.filters.priority = ''
    state.filters.assignedTo = ''
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchTickets,
    fetchTicketById,
    createTicket,
    updateTicket,
    deleteTicket,
    updateTicketStatus,
    assignTicket,
    convertToRequirement,
    setFilters,
    clearFilters,
  }
}

export type TicketsActions = ReturnType<typeof createActions>
